// src/pages/PricingPage.tsx
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.tsx';
import { startCheckout } from '../lib/apiClient';
import { ACCENT } from '../components/Landing/landingData';
import Logo from '../components/Landing/Logo';

const SILK = "'Silkscreen', monospace";
const PIXEL = "'Press Start 2P', monospace";
const VT = "'VT323', monospace";

interface Tier {
  name: string;
  price: string;
  cadence: string;
  features: string[];
  highlight: boolean;
}

interface Faq { q: string; a: string; }

const TIERS: Tier[] = [
  {
    name: 'FREE', price: '$0', cadence: 'forever',
    features: [
      'Eras 01–02 — thesis, market map, beachhead',
      'The Council: Overseer, Elder, Scribe, Auditor',
      'The Scout + The Investigator',
      'Your first boss fight, fully validated',
      'Every file you produce is yours to keep',
    ],
    highlight: false,
  },
  {
    name: 'FOUNDER', price: '$30', cadence: '/ mo',
    features: [
      'Eras 01 → 06 — idea all the way to scale',
      'All 10 agents unlocked',
      'All 33 missions and 6 boss fights',
      'Ambient cadence: weekly journal, monthly memo, quarterly review',
      'Strategic review: continue / pivot / kill',
    ],
    highlight: true,
  },
];

// Pricing-page objections only — the product objections live on the landing FAQ.
const PRICING_FAQ: Faq[] = [
  {
    q: 'What happens when I cancel?',
    a: 'Your vault stays yours. Every market map, interview note, canvas and deck is plain markdown and PDF — export it and keep working elsewhere. Cancelling locks the later eras, not your files.',
  },
  {
    q: 'How far can I really get for free?',
    a: 'All the way through Era 02 — founder thesis, archetype, market map, 30 opportunities and your beachhead — and through the two boss fights that validate them. No credit card, no trial clock. You will know if the bunker suits you before you pay anything.',
  },
  {
    q: 'Why so cheap for ten agents?',
    a: 'Because the alternative is not another SaaS subscription — it is giving away half your company, or building alone. $30 a month is priced to be an obvious yes for a founder with no revenue yet.',
  },
  {
    q: 'Can I pay to skip a boss fight?',
    a: 'No. The gates read the actual files in your vault; money does not create files. Founder access unlocks the later eras — it never unlocks a verdict. No pay to win.',
  },
];

export default function PricingPage() {
  const { session } = useAuth();
  const navigate = useNavigate();

  function onFounder() {
    if (!session) { navigate('/app'); return; }
    startCheckout().catch((err) => console.error('Checkout failed:', err));
  }

  return (
    <div style={{ height: '100dvh', overflowX: 'hidden', overflowY: 'auto', background: 'radial-gradient(120% 80% at 70% -10%, #10231a 0%, #0a120d 45%, #070b09 100%)', color: '#c4d2c8', fontFamily: VT }}>
      <style>{`
        .pr-tiers { display: grid; grid-template-columns: repeat(2, 1fr); gap: 22px; }
        @media (max-width: 640px) {
          .pr-tiers { grid-template-columns: 1fr; }
          .pr-wrap { padding: 40px 20px !important; }
        }
      `}</style>
      <div style={{ pointerEvents: 'none', position: 'fixed', inset: 0, zIndex: 90, background: 'repeating-linear-gradient(to bottom, rgba(0,0,0,0.16) 0px, rgba(0,0,0,0.16) 1px, transparent 1px, transparent 3px)', mixBlendMode: 'multiply' }} />

      {/* NAV (compact, reused idiom) */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 66, padding: '0 34px', background: 'rgba(8,13,10,0.86)', backdropFilter: 'blur(6px)', borderBottom: '1px solid #1a2a20' }}>
        <Link to="/" aria-label="FOUNDR-XI — home"><Logo size={13} /></Link>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/" style={{ fontFamily: SILK, fontSize: 10, letterSpacing: 1, color: '#b9c6bd', padding: '9px 16px', border: '1px solid #2a3a2f' }}>← HOME</Link>
          <Link to="/app" style={{ fontFamily: SILK, fontSize: 10, letterSpacing: 1, color: '#06110a', background: ACCENT, padding: '10px 18px', border: `1px solid ${ACCENT}`, boxShadow: `0 0 18px -4px ${ACCENT}` }}>LOG IN</Link>
        </div>
      </nav>

      <div className="pr-wrap" style={{ maxWidth: 900, margin: '0 auto', padding: '72px 34px' }}>
        <div style={{ fontFamily: SILK, fontSize: 12, letterSpacing: 3, color: ACCENT, marginBottom: 12, textAlign: 'center' }}>CHOOSE YOUR CLEARANCE</div>
        <h1 style={{ fontFamily: PIXEL, fontWeight: 400, fontSize: 26, lineHeight: 1.5, margin: '0 0 20px', color: '#f2ecdc', textAlign: 'center', letterSpacing: -0.5 }}>PRICING</h1>

        {/* Value anchor: the real alternative to this product is equity or solitude,
            not another subscription. Say it before the numbers. */}
        <p style={{ fontSize: 21, lineHeight: 1.35, color: '#a6b4aa', textAlign: 'center', maxWidth: 620, margin: '0 auto 46px' }}>
          A cofounder costs half your company. A consultant costs your runway.
          <br />
          <span style={{ color: ACCENT }}>Ten specialists cost less than one dinner out.</span>
        </p>

        <div className="pr-tiers">
          {TIERS.map((t) => (
            <div key={t.name} style={{ background: '#0b120e', border: `1px solid ${t.highlight ? ACCENT : '#1c2b22'}`, borderTop: `3px solid ${t.highlight ? ACCENT : '#2a3a2f'}`, padding: 26, boxShadow: t.highlight ? `0 0 40px -18px ${ACCENT}` : 'none' }}>
              <div style={{ fontFamily: SILK, fontSize: 12, letterSpacing: 1, color: '#eaddc6', marginBottom: 14 }}>{t.name}</div>
              <div style={{ marginBottom: 22 }}>
                <span style={{ fontFamily: PIXEL, fontSize: 26, color: ACCENT }}>{t.price}</span>
                <span style={{ fontFamily: VT, fontSize: 18, color: '#8fa196', marginLeft: 8 }}>{t.cadence}</span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 26px' }}>
                {t.features.map((f) => (
                  <li key={f} style={{ fontSize: 17, lineHeight: 1.3, color: '#a6b4aa', marginBottom: 12, display: 'flex', gap: 8 }}>
                    <span style={{ color: ACCENT }}>▮</span><span>{f}</span>
                  </li>
                ))}
              </ul>
              {t.highlight ? (
                <button onClick={onFounder} style={{ width: '100%', fontFamily: SILK, fontSize: 11, letterSpacing: 1, color: '#06110a', background: ACCENT, padding: '14px 0', border: `1px solid ${ACCENT}`, boxShadow: `0 0 26px -8px ${ACCENT}`, cursor: 'pointer' }}>UNLOCK FOUNDER →</button>
              ) : (
                <Link to="/app" style={{ display: 'block', textAlign: 'center', fontFamily: SILK, fontSize: 11, letterSpacing: 1, color: '#d6e0d8', padding: '14px 0', border: '1px solid #33473a', background: 'rgba(20,32,25,0.5)' }}>START FREE →</Link>
              )}
            </div>
          ))}
        </div>
        <p style={{ textAlign: 'center', fontFamily: SILK, fontSize: 8, letterSpacing: 1, color: '#5a6a5f', margin: '34px 0 0' }}>NO PAY TO WIN. CANCEL ANYTIME.</p>

        <div style={{ marginTop: 64 }}>
          <div style={{ fontFamily: SILK, fontSize: 11, letterSpacing: 2, color: ACCENT, marginBottom: 22, textAlign: 'center' }}>BEFORE YOU DECIDE</div>
          {PRICING_FAQ.map((f) => (
            <div key={f.q} style={{ borderTop: '1px solid #1c2b22', padding: '18px 0' }}>
              <div style={{ fontFamily: VT, fontSize: 22, lineHeight: 1.2, color: '#e8ddc7', marginBottom: 8 }}>{f.q}</div>
              <p style={{ fontSize: 18, lineHeight: 1.34, color: '#8f9f94', margin: 0 }}>{f.a}</p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 44, textAlign: 'center' }}>
          <Link to="/#faq" style={{ fontFamily: SILK, fontSize: 10, letterSpacing: 1, color: '#b9c6bd', padding: '13px 22px', border: '1px solid #2a3a2f', display: 'inline-block' }}>
            MORE QUESTIONS? ASK THE CREW →
          </Link>
        </div>
      </div>
    </div>
  );
}
